import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Bot, User, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useLocation } from "react-router-dom";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-chat`;

export default function ChatWidget() {
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Hallo! 👋 Ich bin der Table-Guide-Assistent. Wie kann ich dir helfen?" },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { isListening, secondsLeft, start: startListening, stop: stopListening, isSupported: speechSupported } = useSpeechRecognition({
    lang: "de-DE",
    onTranscript: (text) => setInput(text),
  });

  // Listen for trigger events from the navbar button
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-chat-widget", handler);
    return () => window.removeEventListener("open-chat-widget", handler);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = useCallback(async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || isLoading) return;

    const userMsg: Message = { role: "user", content: text };
    if (!textOverride) setInput("");
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    let assistantSoFar = "";

    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: [...messages, userMsg] }),
      });

      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({ error: "Fehler" }));
        setMessages((prev) => [...prev, { role: "assistant", content: err.error || "Etwas ist schiefgelaufen. Bitte versuche es später erneut." }]);
        setIsLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";

      const upsert = (chunk: string) => {
        assistantSoFar += chunk;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && prev.length > 1 && prev[prev.length - 2]?.role === "user") {
            return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
          }
          return [...prev, { role: "assistant", content: assistantSoFar }];
        });
      };

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, idx);
          textBuffer = textBuffer.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "" || !line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") { streamDone = true; break; }
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) upsert(content);
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Verbindungsfehler. Bitte versuche es erneut." }]);
    }

    setIsLoading(false);
  }, [input, isLoading, messages]);

  const handleVoiceToggle = useCallback(() => {
    if (isListening) stopListening();
    else startListening();
  }, [isListening, stopListening, startListening]);

  if (isLanding) return null;
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-start md:items-center justify-center p-3 md:p-6 bg-background/70 backdrop-blur-sm animate-in fade-in"
      onClick={() => { setOpen(false); stopListening(); }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:w-[420px] max-w-[calc(100vw-1rem)] h-[calc(100vh-6rem)] md:h-[560px] max-h-[85vh] mt-16 md:mt-0 bg-background border-2 border-border rounded-2xl shadow-2xl flex flex-col animate-in fade-in zoom-in-95"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <Bot className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Table-Guide Support</p>
              <p className="text-xs text-muted-foreground">KI-Assistent • Sprache & Text</p>
            </div>
          </div>
          <button onClick={() => { setOpen(false); stopListening(); }} className="p-1.5 rounded-lg hover:bg-secondary">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((msg, i) => (
            <div key={i} className={cn("flex gap-2", msg.role === "user" ? "justify-end" : "justify-start")}>
              {msg.role === "assistant" && (
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-3 h-3 text-primary" />
                </div>
              )}
              <div
                className={cn(
                  "max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap",
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-secondary text-secondary-foreground rounded-bl-sm"
                )}
              >
                {msg.content}
              </div>
              {msg.role === "user" && (
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-1">
                  <User className="w-3 h-3 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}
          {isLoading && messages[messages.length - 1]?.role === "user" && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Bot className="w-3 h-3 text-primary" />
              </div>
              <div className="bg-secondary rounded-2xl rounded-bl-sm px-3 py-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input with voice */}
        <div className="p-3 border-t border-border">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            {speechSupported && (
              <button
                type="button"
                onClick={handleVoiceToggle}
                className={cn(
                  "shrink-0 h-10 rounded-xl flex items-center justify-center gap-1 transition-all duration-200",
                  isListening
                    ? "bg-destructive text-destructive-foreground animate-pulse shadow-lg px-2.5"
                    : "bg-secondary hover:bg-primary/10 text-muted-foreground hover:text-primary w-10"
                )}
                title={isListening ? `Aufnahme beenden (${secondsLeft}s)` : "Sprachaufnahme (max. 30s)"}
              >
                {isListening ? (
                  <>
                    <MicOff className="w-4 h-4" />
                    <span className="text-xs font-bold tabular-nums">{secondsLeft}s</span>
                  </>
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
            )}
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isListening ? `🎤 Ich höre zu... (${secondsLeft}s)` : "Schreibe eine Nachricht..."}
              className={cn(
                "flex-1 rounded-xl border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isListening ? "border-destructive/40 bg-destructive/5" : "border-input"
              )}
              disabled={isLoading}
            />
            <Button type="submit" size="icon" disabled={!input.trim() || isLoading} className="rounded-xl">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

/** Trigger button to be placed inside the navbar (next to the bell) */
export function ChatWidgetTrigger({ className }: { className?: string }) {
  const location = useLocation();
  if (location.pathname === "/") return null;
  return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent("open-chat-widget"))}
      className={cn(
        "relative w-9 h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all flex items-center justify-center shadow-md hover:scale-105",
        className
      )}
      aria-label="KI-Support öffnen"
      title="KI-Support"
    >
      <MessageCircle className="w-4 h-4" />
    </button>
  );
}
